import React, { useEffect, useState } from "react";
import { Link, NavLink, useNavigate } from "react-router-dom";

import { getUserMeEvents } from "../../../api/user/me";
import useRole, { decodeAuth, hasAnyEventRole } from "./useRole";

const navItemClass = ({ isActive }) =>
  `flex items-center gap-3 px-4 py-2.5 rounded-xl text-sm font-medium transition-colors duration-200 ${
    isActive
      ? "bg-accent-600 text-white shadow-lg shadow-accent-600/20"
      : "text-surface-300 hover:bg-surface-800 hover:text-white"
  }`;

const DashboardLayout = ({ id, children }) => {
  const navigate = useNavigate();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const decoded = decodeAuth();
  // the role this account has on the event being looked at, not on the account
  const { isAdmin, isTicketControl, seesStats } = useRole(id);
  const [events, setEvents] = useState([]);

  useEffect(() => {
    if (!hasAnyEventRole(decoded)) {
      window.location.replace("/");
    }
  }, [decoded]);

  useEffect(() => {
    getUserMeEvents().then((res) => {
      setEvents(res?.data || []);
    });
  }, []);

  const selectedEvent = events?.find((e) => e._id === id);

  const SidebarNav = () => (
    <nav className="space-y-1.5 mt-2">
      <NavLink to={`/dashboard/${id}`} end className={navItemClass}>
        {seesStats ? "📊 Statistika" : "📋 Tədbir"}
      </NavLink>
      <NavLink to={`/dashboard/${id}/tickets`} className={navItemClass}>
        📄 Biletlər
      </NavLink>
      {seesStats && (
        <NavLink to={`/dashboard/${id}/ticket-types`} className={navItemClass}>
          🎟️ Bilet Növləri
        </NavLink>
      )}
      {selectedEvent?.venueId && (
        <NavLink to={`/dashboard/${id}/seat-plan`} className={navItemClass}>
          🪑 Yer planı
        </NavLink>
      )}
      {/* The door scans tickets; the organizer's numbers are not their business */}
      {isTicketControl && (
        <NavLink to="/scan" state={{ from: `/dashboard/${id}/tickets` }} className={navItemClass}>
          📷 Bilet yoxla
        </NavLink>
      )}
      {seesStats && (
        <NavLink to={`/dashboard/${id}/reports`} className={navItemClass}>
          🧾 Hesabatlar
        </NavLink>
      )}
      {isAdmin && (
        <>
          <NavLink to={`/dashboard/${id}/vouchers`} className={navItemClass}>
            🎫 Voucherlar
          </NavLink>
          <NavLink to={`/dashboard/${id}/refunds`} className={navItemClass}>
            💸 Ləğv edilən ödənişlər
          </NavLink>
          <NavLink to="/scan" state={{ from: `/dashboard/${id}` }} className={navItemClass}>
            📷 Bilet yoxla
          </NavLink>
          <NavLink to={`/dashboard/${id}/users`} className={navItemClass}>
            👥 İstifadəçilər
          </NavLink>
          <NavLink to={`/dashboard/${id}/settings`} className={navItemClass}>
            ⚙️ Parametrlər
          </NavLink>
        </>
      )}
    </nav>
  );

  const SidebarContent = (
    <div className="w-72 bg-surface-950 text-white h-full flex flex-col p-5">
      {/* Greeting */}
      <div className="mb-6 px-1">
        <div className="text-xs text-surface-500">Xoş gəldiniz 👋</div>
        <div className="mt-1 text-sm font-semibold text-white break-all">
          {decoded?.email || "Admin"}
        </div>
      </div>

      <NavLink
        to="/dashboard"
        end
        onClick={() => setSidebarOpen(false)}
        className={navItemClass({ isActive: !id })}
      >
        🗂️ Bütün tədbirlər
      </NavLink>

      {/* Selected event context */}
      {id && (
        <div className="mt-5">
          <div className="px-3 mb-2 text-[11px] font-semibold uppercase tracking-wider text-surface-500">
            Seçilmiş tədbir
          </div>
          {selectedEvent && (
            <div className="px-3 py-2 mb-2 text-sm font-semibold text-white bg-surface-900 rounded-xl truncate">
              {selectedEvent.title}
            </div>
          )}
          <SidebarNav />
        </div>
      )}

      {/* Account actions */}
      <div className="mt-auto pt-5 border-t border-surface-800 space-y-1.5">
        <NavLink
          to="/dashboard/change-password"
          onClick={() => setSidebarOpen(false)}
          className={navItemClass}
        >
          🔒 Şifrəni dəyiş
        </NavLink>
        <button
          onClick={() => {
            setSidebarOpen(false);
            navigate("/logout");
          }}
          className="flex items-center gap-3 w-full text-left px-4 py-2.5 rounded-xl text-sm font-medium text-red-400 hover:bg-surface-800 hover:text-red-300 transition-colors duration-200"
        >
          🚪 Çıxış
        </button>
      </div>
    </div>
  );

  return (
    <div className="flex flex-col min-h-screen bg-surface-100">
      {/* Header */}
      <header className="sticky top-0 z-40 bg-surface-950 border-b border-surface-800">
        <div className="flex items-center justify-between h-16 px-4 md:px-6">
          <div className="flex items-center gap-3">
            {/* Hamburger (mobile) */}
            <button
              onClick={() => setSidebarOpen(!sidebarOpen)}
              className="md:hidden text-white p-2 rounded-lg hover:bg-surface-800"
              aria-label="Menyu"
            >
              ☰
            </button>
            <Link to="/" className="flex items-center gap-2.5">
              <div className="w-8 h-8 bg-gradient-to-br from-accent-500 to-pink-500 rounded-lg flex items-center justify-center">
                <span className="text-white font-bold text-xs">T</span>
              </div>
              <span className="text-lg font-bold text-white">
                Tixy<span className="text-accent-400">.az</span>
                <span className="hidden sm:inline text-surface-500 font-normal text-sm"> · Panel</span>
              </span>
            </Link>
          </div>

          {id && (
            <button
              onClick={() => navigate(`/events/${id}`)}
              className="inline-flex items-center gap-2 bg-white/10 text-white border border-white/15 px-3 md:px-4 py-2 rounded-xl text-xs md:text-sm font-medium hover:bg-white/20 transition duration-200"
            >
              🚀 Tədbirə keç
            </button>
          )}
        </div>
      </header>

      <div className="flex flex-1 relative">
        {/* Mobile sidebar */}
        <div
          className={`fixed inset-0 z-30 transition-transform transform duration-300 ease-in-out ${
            sidebarOpen ? "translate-x-0" : "-translate-x-full"
          } md:hidden flex`}
        >
          <div
            className="absolute inset-0 bg-black/50"
            onClick={() => setSidebarOpen(false)}
          />
          <div className="relative z-40 h-full">{SidebarContent}</div>
        </div>

        {/* Desktop sidebar */}
        <aside className="hidden md:block sticky top-16 self-start h-[calc(100vh-4rem)]">
          {SidebarContent}
        </aside>

        {/* Main content */}
        {/* min-w-0: without it a wide table stretches the page instead of
            scrolling inside its own box */}
        <main className="flex-1 min-w-0 p-4 md:p-8 w-full max-w-7xl mx-auto">
          {children}
        </main>
      </div>
    </div>
  );
};

export default DashboardLayout;
